<template>
    <div>
        <div id="Dashboard" class="container is-fluid">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />

            <!-- Header -->
            <HeroContainer title="Match Sale to Quote" />

            <section id="filters" class="filters-section">
                <div class="card shadow-sm search-card filters-card">
                    <div class="card-body filters-body">
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Period</label>
                            </div>
                            <div class="field-body">
                                <div class="field has-addons">
                                    <p class="control">
                                        <span class="select">
                                            <select
                                                id="date_range"
                                                v-model="date_range"
                                                @change="change_date(date_range)"
                                            >
                                                <option
                                                    disabled
                                                    hidden
                                                    value=""
                                                    style="color: grey"
                                                >
                                                    Date Presets
                                                </option>
                                                <option value="today">Today</option>
                                                <option value="week">Week</option>
                                                <option value="this_month">This Month</option>
                                                <option value="prev_month">Prev Month</option>
                                                <option value="this_quarter">This Quarter</option>
                                                <option value="prev_quarter">Prev Quarter</option>
                                                <option value="this_year">This Year</option>
                                                <option value="prev_year">Prev Year</option>
                                                <option value="all">All</option>
                                                <option disabled hidden value="custom">
                                                    Custom
                                                </option>
                                            </select>
                                        </span>
                                    </p>
                                    <p class="control is-expanded">
                                        <VueDatePicker
                                            v-model="range"
                                            :enable-time-picker="false"
                                            range
                                        />
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="buttons is-right filters-actions">
                            <button @click="reset" class="button is-small is-white">Reset</button>
                            <div class="filter-action">
                                <button
                                    @click="ajax_get_dashboard"
                                    class="button is-info"
                                    :disabled="loading"
                                >
                                    Filter
                                </button>
                                <span
                                    v-if="loading"
                                    class="loading-icon"
                                    aria-live="polite"
                                    aria-label="Loading matches"
                                >
                                    <i class="material-icons rotating">.</i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="dashboard_unmatched_sale" v-if="match_data.length > 0">
                <div class="title_div">
                    <span class="site">Unmatched Sale</span>
                </div>
                <div class="table-scroll-wrapper">
                    <TableScrollHeader
                        :can-scroll-left="tableScroll.unmatched.canScrollLeft"
                        :can-scroll-right="tableScroll.unmatched.canScrollRight"
                        :scroll-percent="tableScroll.unmatched.scrollPercent"
                        @scroll-left="scrollHorizontally('unmatched', -1)"
                        @scroll-right="scrollHorizontally('unmatched', 1)"
                    />
                    <div
                        class="table-scroll-container"
                        ref="unmatchedTableScroll"
                        @scroll="updateScrollState('unmatched')"
                    >
                        <SimpleDataTable
                            :headers="unmatchedHeaders"
                            :data="unmatchedSaleRows"
                            :enable-selection="false"
                            :show-controls="false"
                            :default-items-per-page="1"
                            :items-per-page-options="[1]"
                        />
                    </div>
                </div>
            </section>

            <section id="dashboard_quotes" v-if="match_data.length > 0">
                <div class="title_div">
                    <span class="site">Quotes -- Possible Matches</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <button
                            class="button is-info"
                            :disabled="selectedQuoteIds.length !== 1"
                            @click="ajax_create_match"
                        >
                            Create Match
                        </button>
                    </div>
                </div>
                <div class="table-scroll-wrapper">
                    <TableScrollHeader
                        :can-scroll-left="tableScroll.quotes.canScrollLeft"
                        :can-scroll-right="tableScroll.quotes.canScrollRight"
                        :scroll-percent="tableScroll.quotes.scrollPercent"
                        @scroll-left="scrollHorizontally('quotes', -1)"
                        @scroll-right="scrollHorizontally('quotes', 1)"
                    />
                    <div
                        class="table-scroll-container"
                        ref="quotesTableScroll"
                        @scroll="updateScrollState('quotes')"
                    >
                        <SimpleDataTable
                            :headers="quotesHeaders"
                            :data="quotesTableRows"
                            row-key="id"
                            :default-items-per-page="5"
                            :items-per-page-options="[3, 5, 7, 10, 20, 50]"
                            @selection-changed="onQuotesSelectionChanged"
                        />
                    </div>
                </div>
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="$emit('download-csv', [match_data, 'MatchSaleQuote', null])"
                    >
                        Download CSV
                    </button>
                </div>
            </section>

            <section id="dashboard_fuzzy_quotes" v-if="match_data.length > 0">
                <div class="title_div">
                    <span class="site">Quotes -- By Reference</span>
                </div>
                <div class="field">
                    <div class="control">
                        <span class="select">
                            <select id="fuzzy_references" v-model="fuzzy_reference_selected">
                                <option disabled selected value="" style="color: grey">
                                    Quote Reference
                                </option>
                                <option
                                    v-for="fr in fuzzy_references"
                                    :key="'fuzref_' + fr.q_id"
                                    :value="fr.q_ref"
                                >
                                    {{ '[' + fr.distance + ']  ' + fr.q_ref }}
                                </option>
                            </select>
                        </span>
                    </div>
                </div>
                <div v-if="match_data_fuzzy.length > 0">
                    <div class="table-scroll-wrapper">
                        <TableScrollHeader
                            :can-scroll-left="tableScroll.fuzzy.canScrollLeft"
                            :can-scroll-right="tableScroll.fuzzy.canScrollRight"
                            :scroll-percent="tableScroll.fuzzy.scrollPercent"
                            @scroll-left="scrollHorizontally('fuzzy', -1)"
                            @scroll-right="scrollHorizontally('fuzzy', 1)"
                        />
                        <div
                            class="table-scroll-container"
                            ref="fuzzyTableScroll"
                            @scroll="updateScrollState('fuzzy')"
                        >
                            <SimpleDataTable
                                :headers="fuzzyHeaders"
                                :data="fuzzyQuotesTableRows"
                                row-key="id"
                                :default-items-per-page="5"
                                :items-per-page-options="[3, 5, 7, 10, 20, 50]"
                                @selection-changed="onFuzzySelectionChanged"
                            />
                        </div>
                    </div>
                    <div class="buttons is-right">
                        <button
                            class="button is-info"
                            style="margin: 0.5em 0 0 0"
                            @click="
                                $emit('download-csv', [match_data_fuzzy, 'MatchSaleQuote', null])
                            "
                        >
                            Download CSV
                        </button>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<style scoped>
    .title_div {
        margin-top: 1.5em;
    }

    .site {
        font-size: 2em;
    }

    .filters-section {
        display: flex;
        justify-content: center;
    }

    .filters-card {
        width: 100%;
        max-width: 640px;
    }

    .filters-body {
        display: flex;
        flex-direction: column;
        gap: 0.75em;
    }

    .filters-actions {
        margin-top: 0.5em;
        gap: 0.75em;
    }

    .filter-action {
        display: flex;
        align-items: center;
        gap: 0.5em;
    }

    .loading-icon {
        display: flex;
        align-items: center;
        color: #3273dc;
    }

    .rotating {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    .table-scroll-wrapper {
        margin-top: 0.75rem;
    }

    .table-scroll-container {
        overflow-x: auto;
        width: 100%;
    }

    :deep(.responsible-table .table-scroll-header) {
        display: none;
    }

    :deep(.responsible-table .table-scroll-container) {
        overflow-x: visible;
    }
    #Dashboard > section {
        max-width: 100%;
        margin-bottom: 2em;
    }
</style>

<script>
    import moment from 'moment'
    import VueDatePicker from '@vuepic/vue-datepicker'
    import { getDates } from '@/helpers/dates.js'
    import HeroContainer from '@/components/shared/HeroContainer.vue'
    import SimpleDataTable from '@/components/shared/SimpleDataTable.vue'
    import TableScrollHeader from '@/components/shared/TableScrollHeader.vue'
    import dashboardMatchSaleQuoteService from '@/services/dashboardMatchSaleQuote.service'

    export default {
        name: 'DashboardMatchSaleQuote',
        components: {
            VueDatePicker,
            HeroContainer,
            SimpleDataTable,
            TableScrollHeader,
        },
        computed: {
            unmatched_sale() {
                if (this.match_data.length > 0) {
                    return [this.match_data[0]]
                } else {
                    return []
                }
            },
            fuzzy_reference_html() {
                if (this.fuzzy_reference_selected != '') {
                    return this.get_diff_html(
                        this.fuzzy_reference_selected,
                        this.fuzzy_reference_edit_instructions
                    )
                }
                return ''
            },
            match_so_array: {
                get: function () {
                    return this.searchStore.match_so_array
                },
                set: function (match_so_array) {
                    this.searchStore.set_match_so_array(match_so_array)
                },
            },
            so_part_selected: {
                get: function () {
                    return this.searchStore.so_part_selected
                },
                set: function (so_part_selected) {
                    this.searchStore.set_so_part_selected(so_part_selected)
                },
            },
            so_index: {
                get: function () {
                    return this.searchStore.so_index
                },
                set: function (new_idx) {
                    this.searchStore.set_so_index(new_idx)
                },
            },
            is_paged: function () {
                if (this.match_data.length <= 10) {
                    return false
                } else {
                    return true
                }
            },
            is_paged_fuzzy: function () {
                if (this.match_data_fuzzy.length <= 10) {
                    return false
                } else {
                    return true
                }
            },
            unmatchedHeaders() {
                return {
                    so_part_number: 'Part Number',
                    so_customer_pn: 'Part Customer',
                    s_desc: 'Description',
                    reference: 'Reference',
                    unit_cost: 'Unit Cost',
                    unit_price: 'Unit Price',
                    total_cost: 'Total Cost',
                    total_price: 'Total Price',
                    qty_ship: 'Qty Ship',
                    bill_name: 'Bill Name',
                    vendor_name: 'Vendor',
                    dept_name: 'Dept Name',
                    invoice: 'Invoice',
                    company_po_number: 'Company PO',
                    original_po_number: 'Orig PO',
                    invoice_date: 'Invoice Date',
                    post_date: 'Post Date',
                }
            },
            quotesHeaders() {
                return {
                    match_confidence: 'match',
                    part_distance: 'pn dist',
                    match_reference: 'ref',
                    match_customer: 'cust',
                    match_part: 'pn',
                    match_price_qty: 'px qty',
                    match_price_total: 'px',
                    match_price_variance: 'px var',
                    match_unit_cost: 'unit cost',
                    match_unit_variance: 'unit var',
                    match_quantity: 'qty',
                    so_reference: 'S ref',
                    quote_ref: 'Q ref',
                    bill_name: 'S cust',
                    q_customer: 'Q cust',
                    so_part_number: 'S pn',
                    so_customer_pn: 'S pn cust',
                    q_part_number: 'Q pn',
                    part_quoted: 'Q pn quoted',
                    so_unit_cost: 'S unit cost',
                    q_unit_cost: 'Q unit cost',
                    unit_price: 'S unit price',
                    quote: 'Q unit price',
                    total_price: 'S price',
                    ext_quote: 'Q price',
                    qty_ship: 'S qty',
                    quantity: 'Q qty',
                    unit_of_measure: 'Q UOM',
                    s_desc: 'S description',
                    q_desc: 'Q description',
                    notes: 'notes',
                    core: 'core',
                    exchange: 'exchange',
                    s_id: 's_id',
                    q_id: 'q_id',
                    company_po: 'company_po',
                    invoice: 'invoice',
                    part_number: 'part_number',
                    company_po_number: 'company_po_number',
                    total_cost: 's_cost',
                    vendor_name: 'vendor_name',
                    dept_name: 'dept_name',
                    ind_auto_key: 'ind_auto_key',
                    original_po_number: 'original_po_number',
                    qty_ordered: 'qty_ordered',
                    invoice_date: 'invoice_date',
                    post_date: 'post_date',
                    search_action_id: 'search_action_id',
                    condition_code: 'condition_code',
                    customer_pn: 'customer_pn',
                    qty_invoiced: 'qty_invoiced',
                    route_description: 'route_description',
                    auto_match: 'auto_match',
                    match_user_id_id: 'match_user_id_id',
                    quote_id: 'quote_id',
                    lead_time: 'lead_time',
                    vendor: 'vendor',
                    no_quote: 'no_quote',
                    quote_type: 'quote_type',
                    search_finished: 'search_finished',
                    submitted: 'submitted',
                    sales_order_id: 'sales_order_id',
                    quote_finished: 'quote_finished',
                }
            },
            fuzzyHeaders() {
                return this.quotesHeaders
            },
            unmatchedSaleRows() {
                return (this.unmatched_sale || []).map((row, idx) => ({
                    id: row?.s_id || row?.search_action_id || `unmatched-${idx}`,
                    so_part_number: row?.so_part_number,
                    so_customer_pn: row?.so_customer_pn,
                    s_desc: row?.s_desc,
                    reference: row?.reference,
                    unit_cost: this.formatCurrency(row?.unit_cost),
                    unit_price: this.formatCurrency(row?.unit_price),
                    total_cost: this.formatCurrency(row?.total_cost),
                    total_price: this.formatCurrency(row?.total_price),
                    qty_ship: this.formatNumber(row?.qty_ship),
                    bill_name: row?.bill_name,
                    vendor_name: row?.vendor_name,
                    dept_name: row?.dept_name,
                    invoice: row?.invoice,
                    company_po_number: row?.company_po_number,
                    original_po_number: row?.original_po_number,
                    invoice_date: this.formatDate(row?.invoice_date),
                    post_date: this.formatDate(row?.post_date),
                }))
            },
            quotesTableRows() {
                return this.match_data.map((row, idx) => this.buildQuoteRow(row, idx))
            },
            fuzzyQuotesTableRows() {
                return this.match_data_fuzzy.map((row, idx) => this.buildQuoteRow(row, idx))
            },
        },
        data: function () {
            return {
                numeral: require('numeral'),
                figureKey: 0,
                customer: '',
                vendor: '',
                user: '',
                loading: false,
                error: null,
                date_range: 'week',
                range: [moment().startOf('week').toDate(), moment().endOf('week').toDate()],
                so_list: [],
                match_data: [],
                match_data_fuzzy: [],
                fuzzy_reference_edit_instructions: [],
                fuzzy_references: [],
                fuzzy_reference_selected: '',
                fuzzy_key: 0,
                selectedQuoteIds: [],
                selectedFuzzyIds: [],
                tableScroll: {
                    unmatched: { canScrollLeft: false, canScrollRight: false, scrollPercent: 0 },
                    quotes: { canScrollLeft: false, canScrollRight: false, scrollPercent: 0 },
                    fuzzy: { canScrollLeft: false, canScrollRight: false, scrollPercent: 0 },
                },
            }
        },
        watch: {
            so_index() {
                this.SoChange()
            },
            fuzzy_reference_selected() {
                if (this.fuzzy_reference_selected != '') {
                    this.ajax_get_matches_fuzzy_reference()
                }
            },
            unmatchedSaleRows() {
                this.$nextTick(() => this.updateScrollState('unmatched'))
            },
            quotesTableRows() {
                this.$nextTick(() => this.updateScrollState('quotes'))
            },
            fuzzyQuotesTableRows() {
                this.$nextTick(() => this.updateScrollState('fuzzy'))
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.updateScrollState('unmatched')
                this.updateScrollState('quotes')
                this.updateScrollState('fuzzy')
            })
        },
        methods: {
            getDates,
            setLoadingState(isLoading) {
                this.loading = isLoading
            },
            setError(message = null) {
                this.error = message
            },
            formatCurrency(value) {
                if (value === null || value === undefined || value === '') return value
                return this.numeral(value).format('$0,0,0,0,0.00')
            },
            formatNumber(value) {
                if (value === null || value === undefined || value === '') return value
                return this.numeral(value).format('0,0,0,0,0')
            },
            formatDate(value) {
                if (!value) return value
                if (typeof value === 'string' && value.includes('T')) {
                    return value.split('T')[0]
                }
                return value
            },
            formatBoolean(value) {
                if (value === true) return 'T'
                if (value === false) return 'F'
                return value
            },
            quoteRowId(row, idx) {
                return (
                    row?.q_id ||
                    row?.quote_id ||
                    row?.search_action_id ||
                    row?.s_id ||
                    `quote-${idx}`
                )
            },
            buildQuoteRow(row, idx) {
                return {
                    id: this.quoteRowId(row, idx),
                    match_confidence: row?.match_confidence,
                    part_distance: row?.part_distance,
                    match_reference: this.formatBoolean(row?.match_reference),
                    match_customer: this.formatBoolean(row?.match_customer),
                    match_part: this.formatBoolean(row?.match_part),
                    match_price_qty: this.formatBoolean(row?.match_price_qty),
                    match_price_total: this.formatBoolean(row?.match_price_total),
                    match_price_variance: this.formatBoolean(row?.match_price_variance),
                    match_unit_cost: this.formatBoolean(row?.match_unit_cost),
                    match_unit_variance: this.formatBoolean(row?.match_unit_variance),
                    match_quantity: this.formatBoolean(row?.match_quantity),
                    so_reference: row?.so_reference,
                    quote_ref: row?.quote_ref,
                    bill_name: row?.bill_name,
                    q_customer: row?.q_customer,
                    so_part_number: row?.so_part_number,
                    so_customer_pn: row?.so_customer_pn,
                    q_part_number: row?.q_part_number,
                    part_quoted: row?.part_quoted,
                    so_unit_cost: this.formatCurrency(row?.so_unit_cost),
                    q_unit_cost: this.formatCurrency(row?.q_unit_cost),
                    unit_price: this.formatCurrency(row?.unit_price),
                    quote: this.formatCurrency(row?.quote),
                    total_price: this.formatCurrency(row?.total_price),
                    ext_quote: this.formatCurrency(row?.ext_quote),
                    qty_ship: this.formatNumber(row?.qty_ship),
                    quantity: this.formatNumber(row?.quantity),
                    unit_of_measure: row?.unit_of_measure,
                    s_desc: row?.s_desc,
                    q_desc: row?.q_desc,
                    notes: row?.notes,
                    core: this.formatCurrency(row?.core),
                    exchange: row?.exchange,
                    s_id: row?.s_id,
                    q_id: row?.q_id,
                    company_po: row?.company_po,
                    invoice: row?.invoice,
                    part_number: row?.part_number,
                    company_po_number: row?.company_po_number,
                    total_cost: this.formatCurrency(row?.total_cost),
                    vendor_name: row?.vendor_name,
                    dept_name: row?.dept_name,
                    ind_auto_key: row?.ind_auto_key,
                    original_po_number: row?.original_po_number,
                    qty_ordered: this.formatNumber(row?.qty_ordered),
                    invoice_date: this.formatDate(row?.invoice_date),
                    post_date: this.formatDate(row?.post_date),
                    search_action_id: row?.search_action_id,
                    condition_code: row?.condition_code,
                    customer_pn: row?.customer_pn,
                    qty_invoiced: this.formatNumber(row?.qty_invoiced),
                    route_description: row?.route_description,
                    auto_match: row?.auto_match,
                    match_user_id_id: row?.match_user_id_id,
                    quote_id: row?.quote_id,
                    lead_time: row?.lead_time,
                    vendor: row?.vendor,
                    no_quote: row?.no_quote,
                    quote_type: row?.quote_type,
                    search_finished: row?.search_finished,
                    submitted: row?.submitted,
                    sales_order_id: row?.sales_order_id,
                    quote_finished: row?.quote_finished,
                }
            },
            onQuotesSelectionChanged(ids) {
                this.selectedQuoteIds = ids || []
            },
            onFuzzySelectionChanged(ids) {
                this.selectedFuzzyIds = ids || []
            },
            getScrollElement(key) {
                const wrapper = this.$refs[`${key}TableScroll`]
                if (!wrapper) return null
                return wrapper
            },
            updateScrollState(key) {
                const el = this.getScrollElement(key)
                if (!el) return
                const { scrollLeft, scrollWidth, clientWidth } = el
                const maxScroll = Math.max(0, scrollWidth - clientWidth)
                this.tableScroll[key].canScrollLeft = scrollLeft > 0
                this.tableScroll[key].canScrollRight = scrollLeft < maxScroll
                this.tableScroll[key].scrollPercent = maxScroll ? (scrollLeft / maxScroll) * 100 : 0
            },
            scrollHorizontally(key, direction) {
                const el = this.getScrollElement(key)
                if (!el) return
                const amount = el.clientWidth * 0.8 * direction
                el.scrollBy({ left: amount, behavior: 'smooth' })
            },
            reset() {
                this.match_data = []
                this.match_data_fuzzy = []
                this.fuzzy_references = []
                this.fuzzy_reference_selected = ''
                this.fuzzy_reference_edit_instructions = ''
                this.fuzzy_key = 0
                this.customer = ''
                this.vendor = ''
                this.user = ''
                this.date_range = 'week'
                this.range = [moment().startOf('week').toDate(), moment().endOf('week').toDate()]
                this.selectedQuoteIds = []
                this.selectedFuzzyIds = []
            },
            get_diff_html(alternate, instructions) {
                let html = ''
                let a = 0
                for (let i of instructions) {
                    if (i == 'same') {
                        html += alternate[a]
                        a += 1
                    } else if (i == 'del') {
                        html += `<span class='levenshtein_delete'></span>`
                    } else if (i == 'insert') {
                        html += `<span class='levenshtein_insert'>${alternate[a]}</span>`
                        a += 1
                    } else if (i == 'sub') {
                        html += `<span class='levenshtein_sub'>${alternate[a]}</span>`
                        a += 1
                    }
                }
                return html
            },
            SoChange() {
                // Check that the index is within the list length
                if (this.so_index >= this.so_list.length) {
                    this.so_index = 0
                }
                // Set the selected part in case we changed the part via dropdown or edit the arrays
                this.so_part_selected = this.match_so_array[this.so_index]
                let val = this.so_list[this.so_index]['id']
                this.ajax_get_matches(val)
            },
            change_date(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.range = range
                }
            },
            set_searching(is_searching) {
                this.searchStore.set_ajax_searching(is_searching)
            },
            filter_records() {
                this.ajax_get_dashboard()
            },
            async ajax_get_dashboard() {
                this.match_data = []
                this.match_data_fuzzy = []
                this.fuzzy_references = []
                this.fuzzy_reference_selected = ''
                this.fuzzy_reference_edit_instructions = ''
                this.period_last_filtered = this.period
                let begin_date = moment.utc(this.range[0]).format('YYYY-MM-DD')
                let end_date = moment.utc(this.range[1]).endOf('day').format('YYYY-MM-DD')
                this.set_searching(true)
                this.setLoadingState(true)
                this.setError(null)
                try {
                    const response = await dashboardMatchSaleQuoteService.fetchDashboardByDate(
                        begin_date,
                        end_date
                    )
                    if (response?.success) {
                        const { match_data, so_list, fuzzy_references } = response.data || {}
                        const so_part_list = (so_list || []).map((so) => so.part_number)
                        this.searchStore.set_match_so_array(so_part_list)
                        this.match_data = match_data || []
                        this.fuzzy_references = fuzzy_references || []
                        this.so_list = so_list || []
                        this.so_index = 0
                        this.$nextTick(() => {
                            this.updateScrollState('unmatched')
                            this.updateScrollState('quotes')
                        })
                    } else {
                        this.setError(response?.error || 'Failed to load dashboard data')
                    }
                } catch (error) {
                    this.setError(error instanceof Error ? error.message : 'Unknown error')
                    console.error(JSON.stringify(error?.message || error))
                } finally {
                    this.set_searching(false)
                    this.setLoadingState(false)
                }
            },
            async ajax_get_matches(so_id) {
                this.match_data = []
                this.match_data_fuzzy = []
                this.fuzzy_reference_selected = ''
                this.set_searching(true)
                this.setLoadingState(true)
                this.setError(null)
                try {
                    const response = await dashboardMatchSaleQuoteService.fetchMatches(so_id)
                    if (response?.success) {
                        const { match_data, fuzzy_references } = response.data || {}
                        this.match_data = match_data || []
                        this.fuzzy_references = fuzzy_references || []
                        this.$nextTick(() => {
                            this.updateScrollState('unmatched')
                            this.updateScrollState('quotes')
                        })
                    } else {
                        this.setError(response?.error || 'Failed to load matches')
                    }
                } catch (error) {
                    this.setError(error instanceof Error ? error.message : 'Unknown error')
                    console.error(JSON.stringify(error?.message || error))
                } finally {
                    this.set_searching(false)
                    this.setLoadingState(false)
                }
            },
            async ajax_get_matches_fuzzy_reference() {
                this.match_data_fuzzy = []
                let so_id = this.so_list[this.so_index]['id']
                let quote_reference = this.fuzzy_reference_selected
                this.set_searching(true)
                this.setLoadingState(true)
                this.setError(null)
                try {
                    const response =
                        await dashboardMatchSaleQuoteService.fetchMatchesByFuzzyReference(
                            so_id,
                            quote_reference
                        )
                    if (response?.success) {
                        const { match_data_fuzzy, quote_reference_edits } = response.data || {}
                        this.match_data_fuzzy = match_data_fuzzy || []
                        this.fuzzy_reference_edit_instructions = quote_reference_edits || []
                        this.$nextTick(() => {
                            this.updateScrollState('fuzzy')
                        })
                    } else {
                        this.setError(response?.error || 'Failed to load fuzzy matches')
                    }
                } catch (error) {
                    this.setError(error instanceof Error ? error.message : 'Unknown error')
                    console.error(JSON.stringify(error?.message || error))
                } finally {
                    this.set_searching(false)
                    this.setLoadingState(false)
                }
            },
            async ajax_create_match() {
                this.set_searching(true)
                this.setLoadingState(true)
                this.setError(null)
                const selectedId = this.selectedQuoteIds?.[0]
                if (!selectedId) {
                    alert('Please select one quote to match')
                    this.set_searching(false)
                    this.setLoadingState(false)
                    return
                }

                const rowIndex = this.match_data.findIndex(
                    (item, idx) => this.quoteRowId(item, idx) === selectedId
                )
                if (rowIndex === -1) {
                    alert('Selected quote not found')
                    this.set_searching(false)
                    this.setLoadingState(false)
                    return
                }

                const row = this.match_data[rowIndex]

                try {
                    const response = await dashboardMatchSaleQuoteService.createMatch({
                        saleId: row.s_id,
                        quoteId: row.q_id,
                        searchActionId: row.search_action_id,
                        soIndex: this.so_index,
                    })

                    if (response?.success) {
                        const so_index = response.data?.so_index ?? rowIndex
                        this.match_so_array.splice(so_index, 1)
                        this.so_list.splice(so_index, 1)
                        this.SoChange()
                        this.selectedQuoteIds = []
                        this.$nextTick(() => {
                            this.updateScrollState('unmatched')
                            this.updateScrollState('quotes')
                        })
                    } else {
                        this.setError(response?.error || 'Failed to create match')
                    }
                } catch (error) {
                    this.setError(error instanceof Error ? error.message : 'Unknown error')
                    console.error(JSON.stringify(error?.message || error))
                } finally {
                    this.set_searching(false)
                    this.setLoadingState(false)
                }
            },
        },
    }
</script>
